import React, { useState, useRef, useEffect, useCallback } from 'react';
import {
  Button,
  Modal,
  Form,
  InputNumber,
  Input,
  message,
  Typography,
} from 'antd';
import { EditOutlined, DeleteOutlined } from '@ant-design/icons';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  useDraggable,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import {
  CSS,
} from '@dnd-kit/utilities';
import { ReactComponent as TimerIcon } from 'assets/timer.svg';
import { imgUrl } from 'utils/url';

// White version of media.svg for TimelineSequence
const MediaSvgIconWhite = () => (
  <svg width="12" height="12" viewBox="0 0 25 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M15.5 22.75H9.5C4.07 22.75 1.75 20.43 1.75 15V9C1.75 3.57 4.07 1.25 9.5 1.25H15.5C20.93 1.25 23.25 3.57 23.25 9V15C23.25 20.43 20.93 22.75 15.5 22.75ZM9.5 2.75C4.89 2.75 3.25 4.39 3.25 9V15C3.25 19.61 4.89 21.25 9.5 21.25H15.5C20.11 21.25 21.75 19.61 21.75 15V9C21.75 4.39 20.11 2.75 15.5 2.75H9.5Z" fill="#ffffff"/>
    <path d="M9.5 10.75C7.98 10.75 6.75 9.52 6.75 8C6.75 6.48 7.98 5.25 9.5 5.25C11.02 5.25 12.25 6.48 12.25 8C12.25 9.52 11.02 10.75 9.5 10.75ZM9.5 6.75C8.81 6.75 8.25 7.31 8.25 8C8.25 8.69 8.81 9.25 9.5 9.25C10.19 9.25 10.75 8.69 10.75 8C10.75 7.31 10.19 6.75 9.5 6.75Z" fill="#ffffff"/>
    <path d="M3.17002 19.7001C2.93002 19.7001 2.69002 19.5801 2.55002 19.3701C2.32002 19.0301 2.41002 18.5601 2.76002 18.3301L7.69002 15.0201C8.77002 14.2901 10.26 14.3801 11.24 15.2101L11.57 15.5001C12.07 15.9301 12.92 15.9301 13.41 15.5001L17.57 11.9301C18.63 11.0201 20.3 11.0201 21.37 11.9301L23 13.3301C23.31 13.6001 23.35 14.0701 23.08 14.3901C22.81 14.7001 22.34 14.7401 22.02 14.4701L20.39 13.0701C19.89 12.6401 19.04 12.6401 18.54 13.0701L14.38 16.6401C13.32 17.5501 11.65 17.5501 10.58 16.6401L10.25 16.3501C9.79002 15.9601 9.03002 15.9201 8.52002 16.2701L3.59002 19.5801C3.46002 19.6601 3.31002 19.7001 3.17002 19.7001Z" fill="#ffffff"/>
  </svg>
);
import './index.css';

const { Title } = Typography;

const TimelineSequence = ({
  duration = 60,
  programmaticVendors = [],
  allMediaList,
  initialSlots = [],
  onSlotsChange,
  isReadOnly = false,
  mediaList = [],
}) => {
  const [slots, setSlots] = useState(initialSlots);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingSlot, setEditingSlot] = useState(null);
  const [newSlotData, setNewSlotData] = useState({
    mediaType: 'media',
    duration: 10,
    programmaticVendor: null,
    priority: 1,
    name: '',
    medias: [],
  });
  const [isPreviewModalVisible, setIsPreviewModalVisible] = useState(false);
  const [previewSlot, setPreviewSlot] = useState(null);
  const draggedIdRef = useRef(null);
  const [isResizing, setIsResizing] = useState(false);
  const [livePosition, setLivePosition] = useState(null);
  const [draggedSlot, setDraggedSlot] = useState(null)
  const resizeRef = useRef({
    resizedSlot: null,
    startX: 0,
    originalDuration: 0,
    resizedElement: null,
  });

  // DndKit sensors for timeline
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor)
  );

  // DndKit sensors for media sorting
  const mediaSensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const timelineRef = useRef(null);
  const timelineGridRef = useRef(null);
  const rowHeight = 70;
  const minRows = 3;

  // Timeline width calculation - minimum 1200px, scales with timeline length
  const timelineWidth = Math.max(1200, duration * 8);
  const pixelsPerSecond = timelineWidth / duration;


  // Check if position has collision

  const hasCollision = (startTime, row, duration, excludeSlotId, allSlots) => {
    const numRow = Number(row)
    const numStartTime = Number(startTime)
    const newEndTime = numStartTime + Number(duration)

    const collision = allSlots.some((slot) => {
      if (slot.id === excludeSlotId) return false

      const existingRow = Number(slot.row)
      const existingStartTime = Number(slot.startTime)
      const existingDuration = Number(slot.duration)
      const slotEndTime = existingStartTime + existingDuration

      const isSameRow = existingRow === numRow
      const isOverlapping = !(slotEndTime <= numStartTime || existingStartTime >= newEndTime)

      return isSameRow && isOverlapping
    })

    return collision
  }

  // DndKit event handlers
  const handleDragStart = (event) => {
    draggedIdRef.current = event.active.id;
    setDraggedSlot(event.active.data.current.slot)
  };

  const handleDragOver = (event) => {}

  const handleDragEnd = (event) => {
    const { active, delta, over } = event
    const draggedId = draggedIdRef.current

    // Check if a drag operation was properly started
    if (!draggedId || !active) {
      draggedIdRef.current = null
      setDraggedSlot(null)
      return
    }

    setSlots((currentSlots) => {
      const slotBeingDragged = currentSlots.find((s) => s.id === draggedId)

      // If for some reason the slot isn't found, cancel the operation
      if (!slotBeingDragged) {
        return currentSlots
      }


      // --- 1. CALCULATE NEW POSITION ---
      // Get the timeline container bounds for accurate positioning
      const timelineRect = timelineRef.current?.getBoundingClientRect()
      if (!timelineRect) {
        return currentSlots
      }


      // Calculate absolute position based on current slot position + drag delta
      const currentSlotPixelX = slotBeingDragged.startTime * pixelsPerSecond
      const currentSlotPixelY = slotBeingDragged.row * rowHeight

      const finalPixelX = currentSlotPixelX + delta.x
      const finalPixelY = currentSlotPixelY + delta.y

      // Calculate new row directly from pixel position
      let newRow = Math.round(finalPixelY / rowHeight)
      newRow = Math.max(0, newRow)

      let newStartTimeInSeconds
      let didSnap = false

      // console.log(
      //   "Drag calculation - currentRow:",
      //   slotBeingDragged.row,
      //   "currentPixelY:",
      //   currentSlotPixelY,
      //   "deltaY:",
      //   delta.y,
      //   "finalPixelY:",
      //   finalPixelY,
      //   "calculatedRow:",
      //   newRow,
      // )

      // --- 2. ENHANCED SNAPPING LOGIC ---
      const snapThresholdInPixels = 8 // Reduced from 15 to allow more precise positioning
      const otherSlotsOnRow = currentSlots.filter((s) => s.id !== draggedId && s.row === newRow)

      // First, calculate the raw time position without snapping
      const rawTimeFromPixels = finalPixelX / pixelsPerSecond

      for (const otherSlot of otherSlotsOnRow) {
        const otherSlotStartPixelX = otherSlot.startTime * pixelsPerSecond
        const otherSlotEndPixelX = (otherSlot.startTime + otherSlot.duration) * pixelsPerSecond

        // Only snap if we're very close to the edges
        // Snap to slot ending (place after the slot)
        if (Math.abs(finalPixelX - otherSlotEndPixelX) <= snapThresholdInPixels) {
          newStartTimeInSeconds = otherSlot.startTime + otherSlot.duration
          didSnap = true
          // console.log("Snapped to end of slot", otherSlot.id, "at time", newStartTimeInSeconds)
          break
        }

        // Snap to slot beginning (place before the slot)
        if (Math.abs(finalPixelX - otherSlotStartPixelX) <= snapThresholdInPixels) {
          newStartTimeInSeconds = Math.max(0, otherSlot.startTime - slotBeingDragged.duration)
          didSnap = true
          // console.log("Snapped to start of slot", otherSlot.id, "at time", newStartTimeInSeconds)
          break
        }
      }


      if (!didSnap) {
        const gridInterval = 1 // Changed from 5/10/30 to 1 second for precise positioning
        newStartTimeInSeconds = Math.round(rawTimeFromPixels / gridInterval) * gridInterval
        // console.log("Grid snapped to", newStartTimeInSeconds, "from raw time", rawTimeFromPixels)

      }


      // --- 4. VALIDATE AND CHECK COLLISION ---
      newStartTimeInSeconds = Math.max(0, Math.min(newStartTimeInSeconds, duration - slotBeingDragged.duration))

      const finalCollision = hasCollision(
        newStartTimeInSeconds,
        newRow,
        slotBeingDragged.duration,
        draggedId,
        currentSlots,
      )

      // console.log(
      //   "Drag end - newRow:",
      //   newRow,
      //   "newStartTime:",
      //   newStartTimeInSeconds,
      //   "collision:",
      //   finalCollision,
      // )

      if (finalCollision) {
        message.error("This position is occupied.")
        return currentSlots // Abort update by returning original state
      }


      // --- 5. UPDATE STATE ---
      const updatedSlots = currentSlots.map((slot) => {
        if (slot.id === draggedId) {
          // console.log("Updating slot from row", slot.row, "to row", newRow)
          return { ...slot, startTime: newStartTimeInSeconds, row: newRow }
        }
        return slot
      })
      onSlotsChange?.(convertSlotsForForm(updatedSlots))
      return updatedSlots
    })

    // Cleanup after the operation
    draggedIdRef.current = null
    setDraggedSlot(null)
  }

  const handleDragCancel = () => {
    setDraggedSlot(null);
    setLivePosition(null);
  };

  // Update slots when initialSlots changes
  useEffect(() => {

      if (initialSlots && initialSlots.length > 0) {
        const convertedSlots = [];

        // Process slots and handle positioning conflicts
        initialSlots.forEach((slot, index) => {
          const mediaType = slot.mediaType === 'MEDIA' ? 'media' :
                          slot.mediaType === 'PROGRAMMATIC' ? 'Programmatic' :
                          slot.mediaType || 'media';

          let finalRow =
            (Array.isArray(slot.rows) && slot.rows.length
              ? slot.rows[0]
              : slot.row) || 0;
          const blockStart = Array.isArray(slot.blocks) && slot.blocks.length
            ? slot.blocks[0]?.startOffset
            : undefined;
          let finalStartTime =
            slot.startTime ?? blockStart ?? 0;

          // Check for conflicts and resolve them
          while (hasCollision(finalStartTime, finalRow, slot.duration || 10, null, convertedSlots)) {
            finalRow++;
          }

          const lookupList = allMediaList || mediaList || [];
          const findById = (id) => lookupList.find((x) => x._id === id);
          const hydrateOne = (m) => {
            if (typeof m === 'string') return findById(m) || { _id: m };
            if (m && !m.logo && m._id) return findById(m._id) || m;
            return m;
          };
          const rawMedias = slot.allMedia || slot.medias || [];
          const hydratedMedias =
            rawMedias.length > 0
              ? rawMedias.map(hydrateOne)
              : (slot.mediaIds || [])
                  .map((id) =>
                    typeof id === 'string' ? findById(id) || { _id: id } : id
                  )
                  .filter(Boolean);

          const convertedSlot = {
            _id: slot._id,
            id: slot._id || slot.id || `${Date.now()}_${index}`,
            mediaType,
            duration: slot.duration || 10,
            startTime: finalStartTime,
            row: finalRow,
            slotNumber: slot.slotNumber || (index + 1),
            priority: slot.priority || 1,
            name: slot.name || '',
            x: slot.x,
            y: slot.y,
            medias: hydratedMedias,
            allMedia: hydratedMedias,
            ...(mediaType === 'Programmatic'
              ? { provider: slot.provider }
              : {}),
          };

          convertedSlots.push(convertedSlot);

        });

        setSlots(convertedSlots);
      } else {
        setSlots([]);
      }
  }, [initialSlots, mediaList, allMediaList]);

  // Generate time markers
  const generateTimeMarkers = () => {
    const markers = [];
    const interval = duration <= 60 ? 5 : duration <= 300 ? 10 : 30;

    for (let i = 0; i <= duration; i += interval) {
      markers.push(
        <div
          key={i}
          className="time-marker"
          style={{
            left: `${(i / duration) * 100}%`,
            position: 'absolute',
            top: 0,
            height: '20px',
            fontSize: '10px',
            paddingLeft: '2px',
            color: '#495057',
          }}
        >
          {i}
        </div>
      );
    }
    return markers;
  };

  // Generate grid lines like in the reference
  const renderGridLines = () => {
    const lines = [];
    const interval = duration <= 60 ? 5 : duration <= 300 ? 10 : 30;

    for (let i = 0; i <= duration; i += interval) {
      lines.push(
        <div
          key={i}
          style={{
            position: 'absolute',
            left: `${(i / duration) * 100}%`,
            top: 0,
            bottom: 0,
            borderLeft: '1px solid #d1d5db',
            opacity: 0.3,
          }}
        />
      );
    }
    return lines;
  };

  // Calculate number of rows needed
  const calculateRowsNeeded = () => {
    if (slots.length === 0) return minRows;

    const maxRow = Math.max(...slots.map((slot) => slot.row || 0));
    return Math.max(minRows, maxRow + 1);
  };

  const handleEditSlot = (slot) => {
    setEditingSlot(slot);
    setNewSlotData({
      mediaType: slot.mediaType,
      duration: slot.duration,
      programmaticVendor: slot.provider || null,
      priority: slot.priority || 1,
      name: slot.name || '',
      medias: slot.medias || [],
    });
    setIsModalVisible(true);
  };

  const handlePreviewSlot = (slot) => {
    setPreviewSlot(slot);
    setIsPreviewModalVisible(true);
  };

  const handleDeleteSlot = (slotToDelete) => {
    const updatedSlots = slots.filter((slot) => slot.id !== slotToDelete.id);
    setSlots(updatedSlots);
    onSlotsChange?.(convertSlotsForForm(updatedSlots));
    message.success('Slot deleted successfully');
  };

  const handleCreateSlot = () => {
    if (!newSlotData.name?.trim()) {
      message.error('Please enter a slot name');
      return;
    }
    if (!newSlotData.duration || newSlotData.duration <= 0) {
      message.error('Please enter a valid duration');
      return;
    }
    if (
      newSlotData.mediaType === 'Programmatic' &&
      !newSlotData.programmaticVendor
    ) {
      message.error('Please select a programmatic vendor');
      return;
    }

    // Use the priority provided by user for both media and programmatic slots
    const priority = newSlotData.priority || 1;

    // Find available position based on priority (tries earliest startTime first)
    const availablePosition = findAvailablePosition(newSlotData.duration, priority);
    const availableRow = availablePosition.row;
    const availableStartTime = availablePosition.startTime;


    // Calculate slot number (simple index-based)
    const slotNumber = slots.length + 1;


    let updatedSlots;

    console.log(newSlotData, 'new slot data')

    if (editingSlot) {
      // Update existing slot
      updatedSlots = slots.map((slot) =>
        slot.id === editingSlot.id
          ? {
              ...slot,
              mediaType: newSlotData.mediaType,
              duration: newSlotData.duration,
              name: newSlotData.name,
              medias: newSlotData.mediaType === 'media' ? newSlotData.medias : slot.medias,
              ...(newSlotData.mediaType === 'Programmatic'
                ? { provider: newSlotData.programmaticVendor }
                : { provider: null }),
            }
          : slot
      );
    } else {
      // Create new slot
      const newSlot = {
        id: Date.now() + Math.random(),
        mediaType: newSlotData.mediaType,
        duration: newSlotData.duration,
        name: newSlotData.name,
        startTime: availableStartTime,
        row: availableRow,
        slotNumber: slotNumber,
        priority: priority,
        medias: newSlotData.mediaType === 'media' ? newSlotData.medias : [],
        ...(newSlotData.mediaType === 'Programmatic'
          ? { provider: newSlotData.programmaticVendor }
          : {}),
      };
      updatedSlots = [...slots, newSlot];
    }
    setSlots(updatedSlots);
    onSlotsChange?.(convertSlotsForForm(updatedSlots));

    setIsModalVisible(false);
    setEditingSlot(null);
    setNewSlotData({
      mediaType: 'media',
      duration: 10,
      programmaticVendor: null,
      priority: 1,
      name: '',
      medias: [],
    });
  };

  // Find available position for a slot.
  // Default lane = row 0; pack horizontally (startTime = sum of prior durations in row).
  // Only overflow to next row if row 0 cannot fit the duration before `duration` end.
  const findAvailablePosition = (slotDuration) => {
    if (slots.length === 0) {
      return { row: 0, startTime: 0 };
    }

    const maxRows = 10;
    for (let row = 0; row < maxRows; row++) {
      const rowSlots = slots
        .filter((s) => Number(s.row) === row)
        .sort((a, b) => a.startTime - b.startTime);
      const lastEnd = rowSlots.length
        ? rowSlots[rowSlots.length - 1].startTime +
          rowSlots[rowSlots.length - 1].duration
        : 0;
      if (lastEnd + slotDuration <= duration) {
        return { row, startTime: lastEnd };
      }
    }

    const maxEndTime = Math.max(
      ...slots.map((slot) => slot.startTime + slot.duration)
    );
    return { row: 0, startTime: maxEndTime };
  };


  // Handle mouse down for resizing (keep separate from drag-and-drop)
  const handleResizeMouseDown = (e, slot, elementRef) => {
    e.preventDefault();
    e.stopPropagation();

    resizeRef.current = {
      resizedSlot: slot,
      startX: e.clientX,
      originalDuration: slot.duration,
      resizedElement: elementRef,
    };
    setIsResizing(true);
  };

  // Handle mouse move for resizing only
  const handleMouseMove = useCallback(
    (e) => {
      // Handle resizing visuals
      if (isResizing && resizeRef.current.resizedElement) {
        const deltaX = e.clientX - resizeRef.current.startX;
        const newDuration = Math.max(
          1,
          resizeRef.current.originalDuration + Math.round(deltaX / pixelsPerSecond)
        );

        // Directly update the DOM element width
        const newWidth = newDuration * pixelsPerSecond;
        const element = resizeRef.current.resizedElement;
        if (element) {
          element.style.width = `${newWidth}px`;

          // Update duration display if it exists
          const durationSpan = element.querySelector('.duration-display');
          if (durationSpan) {
            durationSpan.textContent = `${newDuration}s Duration`;
          }
        }

        // Store the current duration in the ref for final update
        resizeRef.current.currentDuration = newDuration;
      }
    },
    [isResizing, pixelsPerSecond]
  );

  const handleMouseUp = useCallback(
    () => {
      // Finalize the resize operation
      if (isResizing && resizeRef.current.resizedSlot && resizeRef.current.currentDuration) {
        // Update the actual slots with the final duration
        const updatedSlots = slots.map((slot) =>
          slot.id === resizeRef.current.resizedSlot.id
            ? { ...slot, duration: resizeRef.current.currentDuration }
            : slot
        );
        setSlots(updatedSlots);
        onSlotsChange?.(convertSlotsForForm(updatedSlots));
      }

      // Reset resize state
      setIsResizing(false);
      resizeRef.current = {
        resizedSlot: null,
        startX: 0,
        originalDuration: 0,
        resizedElement: null,
        currentDuration: 0,
      };
    },
    [isResizing, slots, onSlotsChange]
  );

  // useEffect for resize event listeners only
  useEffect(() => {
    if (isResizing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);

      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isResizing, handleMouseMove, handleMouseUp]);

  // Helper function to render media (same as MediaForm)
  const renderMedia = (media, style, showVideoControls = false) => {
    // Check if it's a video by fileType or by file extension
    const isVideo = (media.fileType && media.fileType.startsWith('video/')) ||
                   (media.mediaUrl && /\.(mp4|mov|avi|wmv|flv|webm|mkv)$/i.test(media.mediaUrl)) ||
                   (media.logo && /\.(mp4|mov|avi|wmv|flv|webm|mkv)$/i.test(media.logo));

    const mediaUrl = media.logo
      ? `${imgUrl}/media/${media.logo}`
      : media.mediaUrl || null;

    if (!mediaUrl) {
      return null;
    }

    return isVideo ? (
      <video
        src={mediaUrl}
        style={style}
        muted
        loop
        preload="metadata"
        {...(!showVideoControls && {
          onMouseEnter: (e) => e.target.play().catch(() => {}),
          onMouseLeave: (e) => e.target.pause()
        })}
      />
    ) : (
      <img
        src={mediaUrl}
        alt={media.name}
        style={style}
        onError={(e) => {
          // Fallback for broken images
          e.target.style.display = 'none';
        }}
      />
    );
  };

  // Get medias from slot for preview
  const getMediasFromSlot = (slot) => {
    // Check for both the new API format (allMedia) and old format (medias)
    let medias = [];

    if (slot.allMedia && Array.isArray(slot.allMedia) && slot.allMedia.length > 0) {
      medias = slot.allMedia;
    } else if (slot.medias && Array.isArray(slot.medias) && slot.medias.length > 0) {
      medias = slot.medias;
    }

    return medias;
  };

  // Render media preview with layout similar to MediaForm
  const renderSlotMediaPreview = (slot) => {
    const medias = getMediasFromSlot(slot);

    // For media slots with no media, show "No media assigned"
    // Check for both 'MEDIA' (from API) and 'media' (after conversion)
    if (medias.length === 0 && (slot.mediaType === 'MEDIA' || slot.mediaType === 'media')) {
      return (
        <div style={{
          width: '40px',
          height: '30px',
          backgroundColor: '#f5f5f5',
          border: '1px dashed #ccc',
          borderRadius: '4px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: '9px',
          color: '#999',
          textAlign: 'center',
          lineHeight: '1.1'
        }}>
          No media assigned
        </div>
      );
    }

    if (medias.length === 0 || (slot.mediaType !== 'media' && slot.mediaType !== 'MEDIA')) {
      return null;
    }

    // Check if any media has issues
    const hasProblematicMedia = medias.some(media =>
      media.isOutdated === true || media.isTargetReached === true
    );

    return (
      <div style={{
        width: '48px',
        height: '48px',
        borderRadius: '4px',
        backgroundColor: '#f5f5f5',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        position: 'relative',
        overflow: 'hidden',
        border: '2px solid rgba(255,255,255,0.8)'
      }}>
        {(() => {
          if (medias.length === 0) {
            // No media - show placeholder
            return (
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <rect x="3" y="3" width="18" height="18" rx="2" stroke="#ccc" strokeWidth="2"/>
                <circle cx="8.5" cy="8.5" r="1.5" fill="#ccc"/>
                <path d="m21 15-3.086-3.086a2 2 0 0 0-2.828 0L6 21" stroke="#ccc" strokeWidth="2"/>
              </svg>
            );
          } else if (medias.length === 1) {
            // 1 media - show full
            const media = medias[0];

            const isProblematic = media.isOutdated === true || media.isTargetReached === true;
            return renderMedia(media, {
              width: '100%',
              height: '100%',

              objectFit: 'cover',
              borderRadius: '4px',
              filter: isProblematic ? 'blur(2px)' : 'none'
            });
          } else if (medias.length === 2) {
            // 2 media - show diagonally
            return (
              <>
                {renderMedia(medias[0], {
                  position: 'absolute',
                  top: '0',
                  left: '0',
                  width: '32px',
                  height: '32px',
                  objectFit: 'cover',
                  borderRadius: '4px 0 0 0',
                  zIndex: 2,
                  filter: hasProblematicMedia ? 'blur(2px)' : 'none'
                })}
                {renderMedia(medias[1], {
                  position: 'absolute',
                  bottom: '0',
                  right: '0',
                  width: '32px',
                  height: '32px',
                  objectFit: 'cover',
                  borderRadius: '0 0 4px 0',
                  zIndex: 1,
                  filter: hasProblematicMedia ? 'blur(2px)' : 'none'
                })}
              </>
            );
          } else if (medias.length === 3) {
            // 3 media - show in grid (2 top, 1 bottom)
            return (
              <>
                {renderMedia(medias[0], {
                  position: 'absolute',
                  top: '0',
                  left: '0',
                  width: '24px',
                  height: '24px',
                  objectFit: 'cover',
                  borderRadius: '4px 0 0 0',
                  filter: hasProblematicMedia ? 'blur(2px)' : 'none'
                })}
                {renderMedia(medias[1], {
                  position: 'absolute',
                  top: '0',
                  right: '0',
                  width: '24px',
                  height: '24px',
                  objectFit: 'cover',
                  borderRadius: '0 4px 0 0',
                  filter: hasProblematicMedia ? 'blur(2px)' : 'none'
                })}
                {renderMedia(medias[2], {
                  position: 'absolute',
                  bottom: '0',
                  left: '50%',
                  transform: 'translateX(-50%)',
                  width: '24px',
                  height: '24px',
                  objectFit: 'cover',
                  borderRadius: '0 0 4px 4px',
                  filter: hasProblematicMedia ? 'blur(2px)' : 'none'
                })}
              </>
            );
          } else if (medias.length === 4) {
            // 4 media - show in 2x2 grid
            return (
              <>
                {renderMedia(medias[0], {
                  position: 'absolute',
                  top: '0',
                  left: '0',
                  width: '24px',
                  height: '24px',
                  objectFit: 'cover',
                  borderRadius: '4px 0 0 0',
                  filter: hasProblematicMedia ? 'blur(2px)' : 'none'
                })}
                {renderMedia(medias[1], {
                  position: 'absolute',
                  top: '0',
                  right: '0',
                  width: '24px',
                  height: '24px',
                  objectFit: 'cover',
                  borderRadius: '0 4px 0 0',
                  filter: hasProblematicMedia ? 'blur(2px)' : 'none'
                })}
                {renderMedia(medias[2], {
                  position: 'absolute',
                  bottom: '0',
                  left: '0',
                  width: '24px',
                  height: '24px',
                  objectFit: 'cover',
                  borderRadius: '0 0 0 4px',
                  filter: hasProblematicMedia ? 'blur(2px)' : 'none'
                })}
                {renderMedia(medias[3], {
                  position: 'absolute',
                  bottom: '0',
                  right: '0',
                  width: '24px',
                  height: '24px',
                  objectFit: 'cover',
                  borderRadius: '0 0 4px 0',
                  filter: hasProblematicMedia ? 'blur(2px)' : 'none'
                })}
              </>
            );
          } else {
            // More than 4 media - show blurred background with count
            return (
              <>
                {renderMedia(medias[0], {
                  position: 'absolute',
                  top: '0',
                  left: '0',
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  borderRadius: '4px',
                  filter: hasProblematicMedia ? 'blur(3px)' : 'blur(2px)',
                  opacity: 0.7
                })}
                <div style={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                  backgroundColor: 'rgba(0, 0, 0, 0.7)',
                  color: 'white',
                  fontSize: '10px',
                  fontWeight: '600',
                  padding: '2px 6px',
                  borderRadius: '8px',
                  textAlign: 'center',
                  zIndex: 10
                }}>
                  {medias.length} Medias
                </div>
              </>
            );
          }
        })()}

        {/* Show overlay if any media has issues */}
        {hasProblematicMedia && (
          <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.6)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: '4px',
            zIndex: 10
          }}>
            <div style={{
              color: 'white',
              fontSize: '8px',
              fontWeight: '600',
              textAlign: 'center',
              lineHeight: '1.2'
            }}>
              ⚠
            </div>
          </div>
        )}
      </div>
    );
  };

  // Sortable media item component
  const SortableMediaItem = ({ media, index, onRemove }) => {
    const {
      attributes,
      listeners,
      setNodeRef,
      transform,
      transition,
      isDragging,
    } = useSortable({ id: media._id });

    const style = {
      transform: CSS.Transform.toString(transform),
      transition,
      opacity: isDragging ? 0.5 : 1,
    };

    return (
      <div
        style={{
          padding: '8px 12px',
          borderBottom: index < newSlotData.medias.length - 1 ? '1px solid #f0f0f0' : 'none',
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          backgroundColor: isDragging ? '#f5f5f5' : 'transparent',
        }}
      >
        {/* Draggable area - everything except the delete button */}
        <div
          ref={setNodeRef}
          style={{
            ...style,
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            flex: 1,
            cursor: isReadOnly ? 'default' : 'grab',
          }}
          {...attributes}
          {...(isReadOnly ? {} : listeners)}
          className={isDragging ? 'dragging' : ''}
        >
          {renderMedia(media, {
            width: '32px',
            height: '24px',
            objectFit: 'cover',
            borderRadius: '2px'
          })}
          <span style={{ fontSize: '12px', flex: 1 }}>{media.name}</span>
        </div>

        {/* Delete button - separate from draggable area */}
        {!isReadOnly && (
          <Button
            type="text"
            danger
            size="small"
            style={{
              padding: '0 4px',
              fontSize: '10px',
              flexShrink: 0,
              zIndex: 10
            }}
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              onRemove();
            }}
          >
            ×
          </Button>
        )}
      </div>
    );
  };

  // Handle media drag end
  const handleMediaDragEnd = (event) => {
    const { active, over } = event;

    if (active.id !== over?.id) {
      setNewSlotData(prev => {
        const oldIndex = prev.medias.findIndex(media => media._id === active.id);
        const newIndex = prev.medias.findIndex(media => media._id === over.id);

        return {
          ...prev,
          medias: arrayMove(prev.medias, oldIndex, newIndex),
        };
      });
    }
  };

  // DraggableSlot component
  const DraggableSlot = ({ slot }) => {
    const slotElementRef = useRef(null);
    const {
      attributes,
      listeners,
      setNodeRef,
      transform,
      isDragging,
    } = useDraggable({
      id: slot.id,
      data: {
        slot,
      },
    });

    const isBeingResized = isResizing && resizeRef.current.resizedSlot?.id === slot.id;

    // Calculate position from slot data
    const left = slot.startTime * pixelsPerSecond;
    const top = slot.row * rowHeight;

    // Apply transform from dnd-kit during drag
    const style = transform ? {
      transform: `translate3d(${transform.x}px, ${transform.y}px, 0)`,
    } : undefined;

    const medias = getMediasFromSlot(slot);

    // Define vendor-specific colors
    const getSlotColor = (slot) => {
      if (slot.mediaType === 'media') {
        return '#4A90E2'; // Blue for AdSign Media
      } else if (slot.mediaType === 'Programmatic') {
        // Find the vendor by provider ID to get the vendor type
        const vendor = programmaticVendors.find((v) => v.provider === slot.provider);
        const vendorType = vendor?.vendorType?.toLowerCase();

        // Different colors for different vendor types
        switch (vendorType) {
          case 'hivestack':
          case 'perion':
            return '#E74C3C'; // Red for Hivestack/Perion
          case 'vistar':
            return '#9B59B6'; // Purple for Vistar
          case 'youtube':
            return '#FF6B6B'; // Coral red for YouTube
          case 'other':
            return '#F39C12'; // Orange for Other
          default:
            return '#95A5A6'; // Gray for unknown vendors
        }
      }
      return '#95A5A6'; // Default gray
    };

    const backgroundColor = getSlotColor(slot);

    // Get slot title using slot name with fallback
    const getSlotTitle = (slot) => {
      const slotNumber = slot.slotNumber || 1;
      const priority = slot.priority || 1;

      // If slot has a name, use it; otherwise fallback to the original format
      if (slot.name && slot.name.trim()) {
        if (slot.mediaType === 'media') {
          return slot.name;
        } else if (slot.mediaType === 'Programmatic') {
          const vendor = programmaticVendors.find((v) => v.provider === slot.provider);
          return `${vendor?.label || slot.provider || 'Vendor\'s Name'} (${slot.name})`;
        }
        return slot.name;
      }

      // Fallback to original format if no name
      if (slot.mediaType === 'media') {
        return `Adsign (Slot ${slotNumber} Priority ${priority})`;
      } else if (slot.mediaType === 'Programmatic') {
        const vendor = programmaticVendors.find((v) => v.provider === slot.provider);
        return `${vendor?.label || slot.provider || 'Vendor\'s Name'} (Slot ${slotNumber} Priority ${priority})`;
      }
      return `Slot ${slotNumber} Priority ${priority}`;
    };

    return (
      <div
        ref={(el) => {
          setNodeRef(el);
          slotElementRef.current = el;
        }}
        className={`timeline-slot ${isDragging ? 'dragging' : ''} ${
          isBeingResized ? 'resizing' : ''
        }`}
        data-type={slot.mediaType}
        style={{
          position: 'absolute',
          left: `${left}px`,
          top: `${top}px`,
          width: `${slot.duration * pixelsPerSecond}px`,
          height: rowHeight + 10,
          backgroundColor,
          border: 'none',
          borderRadius: '8px',
          cursor: isReadOnly ? 'pointer' : isBeingResized ? 'ew-resize' : 'move',
          display: 'flex',
          alignItems: 'center',
          fontSize: '12px',
          color: 'white',
          fontWeight: '500',
          userSelect: 'none',
          zIndex: isDragging || isBeingResized ? 1000 : 2,
          padding: '8px 12px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.15)',
          opacity: 1,
          ...style,
        }}
        {...(isReadOnly || isBeingResized ? {} : { ...attributes, ...listeners })}
        onClick={isReadOnly ? () => handlePreviewSlot(slot) : undefined}
      >
        {/* Slot content layout matching the design */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          width: '100%',
          height: '100%',
        }}>
          {/* Media preview (left side) */}
          {slot.mediaType === 'media' && (
            <div style={{ flexShrink: 0 }}>
              {renderSlotMediaPreview(slot)}
            </div>
          )}

          {/* Text content (right side) */}
          <div style={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            minWidth: 0,
            gap: '2px'
          }}>
            {/* Title */}
            <div style={{
              fontSize: '13px',
              fontWeight: '600',
              lineHeight: '1.2',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap'
            }}>
              {getSlotTitle(slot)}
            </div>

            {/* Duration info */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '4px',
              fontSize: '11px',
              opacity: 0.9,
              lineHeight: '1.2'
            }}>

              <img src={TimerIcon} alt="timer" style={{ width: '12px', height: '12px' }} />
              <span className="duration-display">{slot.duration}s</span>

            </div>

            {/* Media count info */}
            {slot.mediaType === 'media' && medias.length > 0 && (
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                fontSize: '11px',
                opacity: 0.9,
                lineHeight: '1.2'
              }}>
                <MediaSvgIconWhite />
                <span>{medias.length} Media</span>
              </div>
            )}
          </div>
        </div>

        {/* Action buttons (top-right corner) - hidden in read-only mode */}
        {!isReadOnly && (
          <div
            style={{
              position: 'absolute',
              right: '8px',
              top: '8px',
              display: 'flex',
              gap: '4px',
              zIndex: 10,
            }}
          >
            {/* Edit button */}
            <div
              className="edit-handle"
              style={{
                width: '20px',
                height: '20px',
                cursor: 'pointer',
                backgroundColor: 'rgba(255,255,255,0.2)',
                borderRadius: '4px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                opacity: 0.8,
              }}
              onClick={(e) => {
                e.stopPropagation();
                handleEditSlot(slot);
              }}
              onMouseDown={(e) => {
                e.stopPropagation(); // Prevent drag when clicking edit icon
              }}
            >
              <EditOutlined style={{ fontSize: '12px', color: 'white' }} />
            </div>

            {/* Delete button */}
            <div
              className="delete-handle"
              style={{
                width: '20px',
                height: '20px',
                cursor: 'pointer',
                backgroundColor: 'rgba(255,255,255,0.2)',
                borderRadius: '4px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                opacity: 0.8,
              }}
              onClick={(e) => {
                e.stopPropagation();
                handleDeleteSlot(slot);
              }}
              onMouseDown={(e) => {
                e.stopPropagation();
              }}
            >
              <DeleteOutlined style={{ fontSize: '12px', color: 'white' }} />
            </div>
          </div>
        )}

        {/* Resize handle - hidden in read-only mode */}
        {!isReadOnly && (
          <div
            className="resize-handle"
            style={{
              position: 'absolute',
              right: 0,
              top: 0,
              width: '8px',
              height: '100%',
              cursor: 'ew-resize',
              backgroundColor: 'rgba(255,255,255,0.3)',
              borderTopRightRadius: '8px',
              borderBottomRightRadius: '8px',
            }}
            onMouseDown={(e) => {
              e.stopPropagation();
              handleResizeMouseDown(e, slot, slotElementRef.current);
            }}
          />
        )}
      </div>
    );
  };

  // Render slot function - now uses DraggableSlot
  const renderSlot = (slot) => {
    return <DraggableSlot key={slot.id} slot={slot} />;
  };



  // Convert slots to the format expected by the parent component
  const convertSlotsForForm = (slots) => {
    return slots.map((slot, index) => {
      // Use the slot's actual priority value
      const priority = slot.priority || 1;

      // Slot number is just the index + 1 for proper X/Y display
      const slotNumber = index + 1;

      // Convert frontend casing back to backend casing
      const backendMediaType = slot.mediaType === 'media' ? 'MEDIA' :
                              slot.mediaType === 'Programmatic' ? 'PROGRAMMATIC' :
                              slot.mediaType;

      return {
        _id: slot._id,
        mediaType: backendMediaType,
        slotNumber: slotNumber,
        duration: slot.duration,
        priority: priority,
        rows: [slot.row ?? 0],
        row: slot.row ?? 0,
        startTime: slot.startTime ?? 0,
        x: slot.x,
        y: slot.y,
        id: slot.id,
        name: slot.name && slot.name.trim() ? slot.name.trim() : ``,
        medias: slot.medias,
        mediaIds: (slot.medias || []).map((m) => m._id).filter(Boolean),
        ...(slot.mediaType === 'Programmatic' && slot.provider
          ? {
              provider: slot.provider,
            }
          : {}),
      };
    });
  };

  const rowsNeeded = calculateRowsNeeded();

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragStart={handleDragStart}
      onDragOver={handleDragOver}
      onDragEnd={handleDragEnd}
      onDragCancel={handleDragCancel}
    >
      <div className="timeline-sequence">
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '16px',
          }}
        >
          <Title
            level={5}
            style={{ fontSize: '16px', margin: 0, color: '#2c3e50' }}
          >
            Timeline Sequence
          </Title>
          {/* <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => setIsModalVisible(true)}
            style={{ backgroundColor: '#64D5D2', borderColor: '#64D5D2' }}
          >
            Add New Slot
          </Button> */}
        </div>

      <div
        ref={timelineRef}
        className="timeline-container"
        style={{
          position: 'relative',
          width: '100%',
          height: rowsNeeded * rowHeight + 40, // Slightly more space for the new design
          border: '1px solid #e1e5e9',
          borderRadius: '8px',
          backgroundColor: '#f8f9fa', // Lighter background
          overflowX: 'auto',
          overflowY: 'hidden',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
        }}
      >
        <div style={{ width: timelineWidth, position: 'relative' }}>
          {/* Timeline header */}
          <div
            className="timeline-header"
            style={{
              position: 'sticky',
              top: 0,
              left: 0,
              width: '100%',
              height: '35px',
              backgroundColor: '#f8f9fa',
              borderBottom: '2px solid #e1e5e9',
              zIndex: 10,
            }}
          >
            <div
              style={{
                position: 'relative',
                width: timelineWidth,
                height: '100%',
              }}
            >
              {generateTimeMarkers()}
            </div>
          </div>

          {/* Timeline grid */}
          <div
            ref={timelineGridRef}
            className="timeline-grid"
            style={{
              position: 'relative',
              width: timelineWidth,
              height: rowsNeeded * rowHeight,
              backgroundColor: '#f8f9fa',
            }}
          >
            {/* Grid lines */}
            {renderGridLines()}

            {/* Render slots */}
            {slots.map(renderSlot)}

            {/* Add slot button - positioned to the right of last slot - hidden in read-only mode */}
            {!isReadOnly && (() => {
              let buttonLeft = 10;
              let buttonTop = 10;

              if (slots.length > 0) {
                // Group slots by row
                const slotsByRow = {};
                slots.forEach(slot => {
                  const row = slot.row || 0;
                  if (!slotsByRow[row]) {
                    slotsByRow[row] = [];
                  }
                  slotsByRow[row].push(slot);
                });

                // Find the best position for the "Add New Slot" button
                let bestRow = 0;
                let bestStartTime = 0;

                // Check each row to find available space
                for (let row = 0; row <= Math.max(...Object.keys(slotsByRow).map(Number)); row++) {
                  const rowSlots = slotsByRow[row] || [];

                  if (rowSlots.length === 0) {
                    // Empty row, place at beginning
                    bestRow = row;
                    bestStartTime = 0;
                    break;
                  } else {
                    // Find rightmost slot in this row
                    const rightmostInRow = rowSlots.reduce((rightmost, slot) => {
                      const slotEndX = slot.startTime + slot.duration;
                      const rightmostEndX = rightmost.startTime + rightmost.duration;
                      return slotEndX > rightmostEndX ? slot : rightmost;
                    });

                    const potentialStartTime = rightmostInRow.startTime + rightmostInRow.duration + 1;

                    // If there's space in this row, use it
                    if (potentialStartTime * pixelsPerSecond + 120 <= timelineWidth) {
                      bestRow = row;
                      bestStartTime = potentialStartTime;
                      break;
                    }
                  }
                }

                // If no space found in existing rows, go to next row
                if (bestStartTime === 0 && Object.keys(slotsByRow).length > 0) {
                  bestRow = Math.max(...Object.keys(slotsByRow).map(Number)) + 1;
                  bestStartTime = 0;
                }

                buttonLeft = bestStartTime * pixelsPerSecond;
                buttonTop = bestRow * rowHeight + 10;
              }

              return (
                <div
                  style={{
                    position: 'absolute',
                    left: `${buttonLeft}px`,
                    top: `${buttonTop}px`,
                    width: '140px',
                    height: '50px', // Taller to match new slot height
                    border: '2px dashed #4A90E2',
                    borderRadius: '8px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '13px',
                    color: '#4A90E2',
                    cursor: 'pointer',
                    backgroundColor: 'rgba(74, 144, 226, 0.05)',
                    transition: 'all 0.2s ease',
                    fontWeight: '500',
                    zIndex: 100,
                  }}
                  onClick={() => setIsModalVisible(true)}
                  onMouseEnter={(e) => {
                    e.target.style.backgroundColor = 'rgba(74, 144, 226, 0.1)';
                    e.target.style.transform = 'scale(1.02)';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.backgroundColor =
                      'rgba(74, 144, 226, 0.05)';
                    e.target.style.transform = 'scale(1)';
                  }}
                >
                  + Add New Slot
                </div>
              );
            })()}
          </div>
        </div>
      </div>

      {/* Modal for adding new slot - hidden in read-only mode */}
      {!isReadOnly && (
      <Modal
        title={editingSlot ? 'Edit Slot' : 'Add New Slot'}
        open={isModalVisible}
        onCancel={() => {
          setIsModalVisible(false);
          setEditingSlot(null);
          setNewSlotData({
            mediaType: 'media',
            duration: 10,
            programmaticVendor: null,
            priority: 1,
            name: '',
            medias: [],
          });
        }}
        footer={[
          <Button
            key="cancel"
            onClick={() => {
              setIsModalVisible(false);
              setEditingSlot(null);
              setNewSlotData({
                mediaType: 'media',
                duration: 10,
                programmaticVendor: null,
                priority: 1,
                name: '',
                medias: [],
              });
            }}
            style={{ color: '#ff4d4f' }}
          >
            Cancel
          </Button>,
          <Button
            key="submit"
            type="primary"
            onClick={handleCreateSlot}
            style={{
              backgroundColor: '#64D5D2',
              borderColor: '#64D5D2',
              color: '#ffff',
            }}
          >
            {editingSlot ? 'Update' : 'Submit'}
          </Button>,
        ]}
        width={480}
      >
        <Form layout="vertical">
              <Form.Item label="Slot Name" required>
                <Input
                  placeholder="Enter slot name"
                  value={newSlotData.name}
                  onChange={(e) => {
                    const value = e.target.value;
                    setNewSlotData((prev) => ({ ...prev, name: value }));
                  }}
                  style={{ width: '100%' }}
                />
              </Form.Item>

              <Form.Item label="Duration" required>
                <InputNumber
                  placeholder="Duration"
                  min={1}
                  max={duration}
                  value={newSlotData.duration}
                  onChange={(value) =>
                    setNewSlotData((prev) => ({ ...prev, duration: value }))
                  }
                  style={{ width: '100%' }}
                  addonAfter="Sec"
                />
              </Form.Item>

              <Form.Item label="Priority" required>
                <InputNumber
                  placeholder="Priority"
                  min={1}
                  max={99}
                  value={newSlotData.priority}
                  onChange={(value) =>
                    setNewSlotData((prev) => ({ ...prev, priority: value }))
                  }
                  style={{ width: '100%' }}
                />
              </Form.Item>

              {/* Media Management Section - DISABLED: media assignment to slot turned off */}
              {false && (
              <Form.Item label="Media Selection">
                <div style={{
                  border: '1px solid #d9d9d9',
                  borderRadius: '6px',
                  padding: '12px',
                  backgroundColor: '#fafafa'
                }}>
                  {/* Available Media */}
                  <div style={{ marginBottom: '16px' }}>
                    <Typography.Text strong style={{ fontSize: '12px', color: '#666' }}>
                      Available Media:
                    </Typography.Text>
                    <div style={{
                      maxHeight: '120px',
                      overflowY: 'auto',
                      marginTop: '8px',
                      border: '1px solid #e8e8e8',
                      borderRadius: '4px',
                      backgroundColor: 'white'
                    }}>
                      {(mediaList || []).filter(media =>
                        !newSlotData.medias.find(selectedMedia => selectedMedia._id === media._id)
                      ).length === 0 ? (
                        <div style={{ padding: '12px', textAlign: 'center', color: '#999' }}>
                          All media assigned or no media available
                        </div>
                      ) : (
                        (mediaList || []).filter(media =>
                          !newSlotData.medias.find(selectedMedia => selectedMedia._id === media._id)
                        ).map((media) => (
                          <div
                            key={media._id}
                            style={{
                              padding: '8px 12px',
                              borderBottom: '1px solid #f0f0f0',
                              cursor: isReadOnly ? 'default' : 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '8px',
                              ':hover': { backgroundColor: '#f5f5f5' }
                            }}
                            onClick={() => {
                              if (!isReadOnly) {
                                setNewSlotData(prev => ({
                                  ...prev,
                                  medias: [...prev.medias, media]
                                }));
                              }
                            }}
                          >
                            {renderMedia(media, {
                              width: '32px',
                              height: '24px',
                              objectFit: 'cover',
                              borderRadius: '2px'
                            })}
                            <span style={{ fontSize: '12px' }}>{media.name}</span>
                            {!isReadOnly && (
                              <Button
                                type="link"
                                size="small"
                                style={{ marginLeft: 'auto', padding: '0 4px' }}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setNewSlotData(prev => ({
                                    ...prev,
                                    medias: [...prev.medias, media]
                                  }));
                                }}
                              >
                                Add
                              </Button>
                            )}
                          </div>
                        ))
                      )}
                    </div>
                  </div>

                  {/* Selected Media */}
                  <div>
                    <Typography.Text strong style={{ fontSize: '12px', color: '#666' }}>
                      Selected Media ({newSlotData.medias.length}):
                    </Typography.Text>
                    <div style={{
                      maxHeight: '120px',
                      overflowY: 'auto',
                      marginTop: '8px',
                      border: '1px solid #e8e8e8',
                      borderRadius: '4px',
                      backgroundColor: 'white'
                    }}>
                      {newSlotData.medias.length === 0 ? (
                        <div style={{ padding: '12px', textAlign: 'center', color: '#999' }}>
                          No media selected
                        </div>
                      ) : (
                        <DndContext
                          sensors={mediaSensors}
                          collisionDetection={closestCenter}
                          onDragEnd={handleMediaDragEnd}
                        >
                          <SortableContext
                            items={newSlotData.medias.map(media => media._id)}
                            strategy={verticalListSortingStrategy}
                          >
                            {newSlotData.medias.map((media, index) => (
                              <SortableMediaItem
                                key={media._id}
                                media={media}
                                index={index}
                                onRemove={() => {
                                  setNewSlotData(prev => ({
                                    ...prev,
                                    medias: prev.medias.filter(m => m._id !== media._id)
                                  }));
                                }}
                              />
                            ))}
                          </SortableContext>
                        </DndContext>
                      )}
                    </div>
                  </div>
                </div>
              </Form.Item>
              )}
            </Form>
      </Modal>
      )}

      {/* Preview Modal for Read-Only Mode */}
      {isReadOnly && (
        <Modal
          title={`Preview: ${previewSlot?.name || 'Slot'}`}
          open={isPreviewModalVisible}
          onCancel={() => {
            setIsPreviewModalVisible(false);
            setPreviewSlot(null);
          }}
          footer={[
            <Button
              key="close"
              onClick={() => {
                setIsPreviewModalVisible(false);
                setPreviewSlot(null);
              }}
            >
              Close
            </Button>
          ]}
          width={600}
        >
          {previewSlot && (
            <div>
              {/* Slot Info */}
              <div style={{ marginBottom: '20px', padding: '16px', backgroundColor: '#f5f5f5', borderRadius: '8px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                  <Typography.Text strong>Slot Type:</Typography.Text>
                  <Typography.Text>{previewSlot.mediaType === 'media' ? 'Manual Input' : 'Programmatic'}</Typography.Text>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                  <Typography.Text strong>Duration:</Typography.Text>
                  <Typography.Text>{previewSlot.duration}s</Typography.Text>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                  <Typography.Text strong>Start Time:</Typography.Text>
                  <Typography.Text>{previewSlot.startTime}s</Typography.Text>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                  <Typography.Text strong>Priority:</Typography.Text>
                  <Typography.Text>{previewSlot.priority}</Typography.Text>
                </div>
                {previewSlot.mediaType === 'Programmatic' && previewSlot.provider && (
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography.Text strong>Vendor:</Typography.Text>
                    <Typography.Text>{programmaticVendors.find(v => v.provider === previewSlot.provider)?.label || previewSlot.provider}</Typography.Text>
                  </div>
                )}
              </div>

              {/* Media Preview */}
              {previewSlot.mediaType === 'media' && (() => {
                const medias = getMediasFromSlot(previewSlot);
                return medias.length > 0;
              })() && (
                <div>
                  <Typography.Text strong style={{ fontSize: '16px', marginBottom: '16px', display: 'block' }}>
                    Media Content ({getMediasFromSlot(previewSlot).length})
                  </Typography.Text>
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
                    gap: '16px',
                    maxHeight: '400px',
                    overflowY: 'auto'
                  }}>
                    {getMediasFromSlot(previewSlot).map((media, index) => {
                      const isProblematic = media.isOutdated === true || media.isTargetReached === true;
                      let overlayMessage = '';

                      if (media.isOutdated === true && media.isTargetReached === true) {
                        overlayMessage = 'Out of date range and target reached';
                      } else if (media.isOutdated === true) {
                        overlayMessage = 'Out of date range';
                      } else if (media.isTargetReached === true) {
                        overlayMessage = 'Target reached';
                      }

                      return (
                        <div
                          key={media._id}
                          style={{
                            border: '1px solid #d9d9d9',
                            borderRadius: '8px',
                            padding: '12px',
                            backgroundColor: '#fafafa'
                          }}
                        >
                          <div style={{
                            width: '100%',
                            height: '120px',
                            marginBottom: '8px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            backgroundColor: '#f0f0f0',
                            borderRadius: '4px',
                            overflow: 'hidden',
                            position: 'relative'
                          }}>
                            {renderMedia(media, {
                              width: '100%',
                              height: '100%',
                              objectFit: 'cover',
                              borderRadius: '4px',
                              filter: isProblematic ? 'blur(3px)' : 'none'
                            }, true)}

                            {isProblematic && (
                              <div style={{
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                right: 0,
                                bottom: 0,
                                backgroundColor: 'rgba(0, 0, 0, 0.7)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                borderRadius: '4px'
                              }}>
                                <div style={{
                                  color: 'white',
                                  fontSize: '12px',
                                  fontWeight: '600',
                                  textAlign: 'center',
                                  padding: '8px',
                                  lineHeight: '1.3'
                                }}>
                                  {overlayMessage}
                                </div>
                              </div>
                            )}
                          </div>
                          <Typography.Text strong style={{ fontSize: '12px', display: 'block', marginBottom: '4px' }}>
                            {media.name}
                          </Typography.Text>
                          <Typography.Text style={{ fontSize: '11px', color: '#666', display: 'block' }}>
                            Duration: {media.duration}s
                          </Typography.Text>
                          <Typography.Text style={{ fontSize: '11px', color: '#666', display: 'block' }}>
                            Order: {index + 1}
                          </Typography.Text>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* No Media Message */}
              {previewSlot.mediaType === 'media' && getMediasFromSlot(previewSlot).length === 0 && (
                <div style={{
                  padding: '40px',
                  textAlign: 'center',
                  backgroundColor: '#f5f5f5',
                  borderRadius: '8px',
                  border: '2px dashed #d9d9d9'
                }}>
                  <Typography.Text type="secondary" style={{ fontSize: '14px' }}>
                    No media assigned to this slot
                  </Typography.Text>
                </div>
              )}

              {/* Programmatic Slot Message */}
              {previewSlot.mediaType === 'Programmatic' && (
                <div style={{
                  padding: '40px',
                  textAlign: 'center',
                  backgroundColor: '#f5f5f5',
                  borderRadius: '8px',
                  border: '2px dashed #d9d9d9'
                }}>
                  <Typography.Text type="secondary" style={{ fontSize: '14px' }}>
                    Programmatic content will be served by {programmaticVendors.find(v => v.provider === previewSlot.provider)?.label || previewSlot.provider || 'vendor'}
                  </Typography.Text>
                </div>
              )}
            </div>
          )}
        </Modal>
      )}
      </div>

    </DndContext>
  );
};

export default TimelineSequence;
